import type { CommonCellContextV3 } from "./demand_cell_context";
import type { CommonSupplyCellContextV1 } from "./supply_cell_context";
import type {
  CommonInventoryHealthCellContextV1,
  CommonInventoryHealthCellContextV2,
} from "./inventory_health_cell_context";
import type { CommonInventoryHealthUnfulfilledDemandCellContext } from "./inventory_health_unfulfilled_demand_cell_context";
import type {
  CommonRenderedCellContextV1,
  CommonRenderedCellContextV2,
} from "./rendered_cell_context";
import type { CommonPoInboxRowContextV1 } from "./po_inbox_row_context";
import type { CommonSupplyWalkCellContextV1 } from "./supply_walk_cell_context";
import { calculationFirstCellPrompt } from "./cell_explanation_prompt";

/** Rolling transport compatibility for turns created before CommonCellContext.v2. */
export type ForecastCellContextV1 = Readonly<{
  version: "ForecastCellContext.v1";
  surfaceId: "forecast-tracking";
  measure: "forecast";
  monthStartEpochMs: number;
  channel: string;
  displayedValue: string;
}>;

export type CommonCellContextV2 = Readonly<{
  version: "CommonCellContext.v2";
  surfaceId: "forecast-tracking";
  measure: string;
  cellKind: "period_leaf";
  period: Readonly<{
    field: string;
    grain: "month";
    startEpochMs: number;
    endExclusiveEpochMs: number;
  }>;
  dimensions: readonly Readonly<{ field: string; value: string }>[];
  filters: readonly CommonCellFilterV2[];
  postFilters: readonly CommonCellFilterV2[];
  display: Readonly<{
    value: string;
    unit: "units";
    rule: "round_half_even_whole";
  }>;
  state: Readonly<{
    basis: "base";
    edits: "none";
    comparison: false;
  }>;
}>;

export type CommonCellFilterV2 = Readonly<{
  field: string;
  operator: string;
  values: readonly string[];
}>;

type CommonCellFilterInput = readonly [
  field: string,
  operator: string,
  values: readonly string[],
];

export type CommonMetricContext =
  | ForecastCellContextV1
  | CommonCellContextV2
  | CommonCellContextV3
  | CommonSupplyCellContextV1
  | CommonInventoryHealthCellContextV1
  | CommonInventoryHealthCellContextV2
  | CommonInventoryHealthUnfulfilledDemandCellContext
  | CommonRenderedCellContextV1
  | CommonRenderedCellContextV2
  | CommonSupplyWalkCellContextV1
  | CommonPoInboxRowContextV1;

const DECIMAL_PATTERN =
  /^-?(?=(?:[^0-9]*[0-9]){1,38}[^0-9]*$)(?:0|[1-9][0-9]*)(?:\.[0-9]{1,18})?$/;
const MAX_DIMENSIONS = 32;
const MAX_FILTERS = 64;
const MAX_FILTER_VALUES = 256;

const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const boundedText = (value: unknown, maxBytes = 128): string | undefined => {
  if (typeof value !== "string" || !value || value !== value.trim()) {
    return undefined;
  }
  return utf8ByteLength(value) <= maxBytes && !/\p{Cc}/u.test(value)
    ? value
    : undefined;
};

const canonicalDecimal = (value: unknown): string | undefined => {
  const candidate =
    typeof value === "bigint"
      ? value.toString()
      : typeof value === "number" && Number.isFinite(value)
        ? Object.is(value, -0)
          ? "-0"
          : String(value)
        : typeof value === "string"
          ? value
          : "";
  return DECIMAL_PATTERN.test(candidate) ? candidate : undefined;
};

const canonicalDisplayDecimal = (value: unknown): string | undefined => {
  const decimal = canonicalDecimal(value);
  if (!decimal) return undefined;
  const [whole, fraction] = decimal.split(".");
  if (fraction !== undefined && !/^0+$/.test(fraction)) return decimal;
  return whole;
};

const isCanonicalWholeDisplayDecimal = (value: unknown): boolean =>
  typeof value === "string" &&
  !value.includes(".") &&
  value !== "-0" &&
  canonicalDisplayDecimal(value) === value;

const monthEndExclusive = (startEpochMs: number): number | undefined => {
  const month = new Date(startEpochMs);
  if (
    Number.isNaN(month.getTime()) ||
    month.getUTCDate() !== 1 ||
    month.getUTCHours() !== 0 ||
    month.getUTCMinutes() !== 0 ||
    month.getUTCSeconds() !== 0 ||
    month.getUTCMilliseconds() !== 0
  ) {
    return undefined;
  }
  const end = Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 1);
  return Number.isSafeInteger(end) && end > startEpochMs ? end : undefined;
};

const createFilters = (
  value: readonly CommonCellFilterInput[] | undefined,
): readonly CommonCellFilterV2[] | undefined => {
  if (!Array.isArray(value) || value.length > MAX_FILTERS) return undefined;
  const filters: CommonCellFilterV2[] = [];
  for (const candidate of value) {
    if (!Array.isArray(candidate) || candidate.length !== 3) return undefined;
    const field = boundedText(candidate[0]);
    const operator = boundedText(candidate[1]);
    const rawValues = candidate[2];
    if (
      !field ||
      !operator ||
      !Array.isArray(rawValues) ||
      rawValues.length > MAX_FILTER_VALUES
    ) {
      return undefined;
    }
    const values: string[] = [];
    for (const rawValue of rawValues) {
      const filterValue = boundedText(rawValue, 512);
      if (!filterValue) return undefined;
      values.push(filterValue);
    }
    filters.push(
      Object.freeze({ field, operator, values: Object.freeze(values) }),
    );
  }
  return Object.freeze(filters);
};

export function createForecastCellContext(
  input: Readonly<{
    measure: unknown;
    periodField: unknown;
    monthStartEpochMs: unknown;
    segments: readonly string[] | undefined;
    row: Readonly<Record<string, unknown>>;
    filters: readonly CommonCellFilterInput[] | undefined;
    postFilters: readonly CommonCellFilterInput[] | undefined;
    displayedValue: unknown;
  }>,
): CommonCellContextV2 | undefined {
  const measure = boundedText(input.measure);
  const periodField = boundedText(input.periodField);
  const displayedValue = canonicalDisplayDecimal(input.displayedValue);
  if (
    !measure ||
    !periodField ||
    !displayedValue ||
    !Number.isSafeInteger(input.monthStartEpochMs) ||
    (input.monthStartEpochMs as number) <= 0 ||
    !Array.isArray(input.segments) ||
    input.segments.length === 0 ||
    input.segments.length > MAX_DIMENSIONS ||
    !input.row ||
    typeof input.row !== "object" ||
    Array.isArray(input.row)
  ) {
    return undefined;
  }

  const endExclusiveEpochMs = monthEndExclusive(
    input.monthStartEpochMs as number,
  );
  if (!endExclusiveEpochMs) return undefined;

  const dimensions: Array<Readonly<{ field: string; value: string }>> = [];
  const fields = new Set<string>();
  for (const segment of input.segments) {
    const field = boundedText(segment);
    const value = boundedText(input.row[segment], 512);
    if (!field || !value || fields.has(field)) return undefined;
    fields.add(field);
    dimensions.push(Object.freeze({ field, value }));
  }

  const filters = createFilters(input.filters);
  const postFilters = createFilters(input.postFilters);
  if (!filters || !postFilters) return undefined;

  return Object.freeze({
    version: "CommonCellContext.v2",
    surfaceId: "forecast-tracking",
    measure,
    cellKind: "period_leaf",
    period: Object.freeze({
      field: periodField,
      grain: "month",
      startEpochMs: input.monthStartEpochMs as number,
      endExclusiveEpochMs,
    }),
    dimensions: Object.freeze(dimensions),
    filters,
    postFilters,
    display: Object.freeze({
      value: displayedValue,
      unit: "units",
      rule: "round_half_even_whole",
    }),
    state: Object.freeze({ basis: "base", edits: "none", comparison: false }),
  });
}

export function isSupportedForecastCellRecipe(
  context: CommonCellContextV2,
): boolean {
  const channel = context.dimensions[0];
  return (
    context.version === "CommonCellContext.v2" &&
    context.surfaceId === "forecast-tracking" &&
    context.measure === "forecast" &&
    context.cellKind === "period_leaf" &&
    context.period.field === "order_delivery_month" &&
    context.period.grain === "month" &&
    monthEndExclusive(context.period.startEpochMs) ===
      context.period.endExclusiveEpochMs &&
    context.dimensions.length === 1 &&
    channel?.field === "channel" &&
    Boolean(boundedText(channel.value)) &&
    channel.value !== "n/a" &&
    context.filters.length === 0 &&
    context.postFilters.length === 0 &&
    isCanonicalWholeDisplayDecimal(context.display.value) &&
    context.display.unit === "units" &&
    context.display.rule === "round_half_even_whole" &&
    context.state.basis === "base" &&
    context.state.edits === "none" &&
    context.state.comparison === false
  );
}

const formattedDecimal = (value: string): string => {
  const sign = value.startsWith("-") ? "-" : "";
  const unsigned = sign ? value.slice(1) : value;
  const [whole, fraction] = unsigned.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${grouped}${fraction === undefined ? "" : `.${fraction}`}`;
};

export function forecastCellPrompt(
  context: ForecastCellContextV1 | CommonCellContextV2,
): string {
  const monthStartEpochMs =
    context.version === "CommonCellContext.v2"
      ? context.period.startEpochMs
      : context.monthStartEpochMs;
  const channel =
    context.version === "CommonCellContext.v2"
      ? context.dimensions.find((dimension) => dimension.field === "channel")
          ?.value
      : context.channel;
  const displayedValue =
    context.version === "CommonCellContext.v2"
      ? context.display.value
      : context.displayedValue;
  const month = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(monthStartEpochMs));
  return calculationFirstCellPrompt(
    `the ${channel ?? "selected"} forecast of ${formattedDecimal(displayedValue)} units for ${month}`,
  );
}
